<template>
  <router-link
    v-if="visible"
    class="tag-search-sidebar-link"
    :class="{ active, mastery, rail, mobile }"
    :to="{ name: 'tagSearch#index' }"
    data-test="tag-search-link"
    @click="clicked"
  >
    <span
      class="icon"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        :width="iconSize"
        :height="iconSize"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" />
        <path d="M7 7h.01" />
      </svg>
    </span>
    <span class="name">Explore</span>
  </router-link>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

const props = withDefaults(defineProps<{
  // Icon-first layout for the mastery sidebar's narrow rail
  rail?: boolean;
  // Full-width row for the mobile menus
  mobile?: boolean;
}>(), {
  rail: false,
  mobile: false,
});

const store = useStore();
const route = useRoute();
const instance = getCurrentInstance();

const visible = computed(() => !!store.getters['user/get'] && store.getters['tagSearch/enabled']);
const active = computed(() => !!route.name?.toString().includes('tagSearch#'));
const mastery = computed(() => store.getters['app/isMasteryApp']);
const iconSize = computed(() => {
  if (props.rail) return 24;
  return props.mobile ? 20 : 18;
});

function clicked() {
  const source = props.mobile ? 'mobile_menu' : (props.rail ? 'sidebar_rail' : 'sidebar');
  (instance?.proxy as any)?.$analytics?.events.selectedTagSearchLink({ source });
  store.commit('session/toggleMobileMenu', false);
}
</script>

<style lang="scss" scoped>
  .tag-search-sidebar-link {
    align-items: center;
    border-radius: 4px;
    color: #C3CAD3;
    display: flex;
    gap: rem-calc(8);
    margin-bottom: rem-calc(4);
    min-height: rem-calc(37);
    padding: 0 rem-calc(12);
    text-decoration: none;

    &:hover {
      background-image: linear-gradient(to bottom, #2F3845, #252C35);
    }

    &.active {
      background-image: linear-gradient(-180deg, rgba(81, 97, 115, .48) 3%, rgba(59, 72, 87, .48) 98%);
      color: #FFF;
    }

    .icon {
      display: flex;
    }

    .name {
      font-size: rem-calc(12);
      font-weight: $semi-bold;
    }

    &.mastery {
      color: var(--text-tertiary);

      &:hover,
      &.active {
        background: #F1ECFE;
        color: var(--text-primary);
      }
    }

    &.rail {
      flex-direction: column;
      gap: rem-calc(4);
      height: auto;
      min-height: 0;
      padding: rem-calc(8) 0;
      width: 100%;

      &:hover,
      &.active {
        background: transparent;
      }

      .name {
        display: none;
        font-weight: $normal;
        text-align: center;
      }
    }

    &.mobile {
      color: inherit;
      min-height: 0;
      padding: 0;

      &:hover,
      &.active {
        background: transparent;
      }

      .name {
        font-size: inherit;
        font-weight: inherit;
      }
    }
  }
</style>
